import { useEffect, useState } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import type { components } from "@dolfin/api-admin";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type OrganisationUserResponse = components["schemas"]["OrganisationUserResponse"];

const USER_FETCH_LIMIT = 200;
const NONE = "__none__";

export function memberLabel(member: { name?: string | null; email?: string | null } | null | undefined) {
  return member?.name?.trim() || member?.email || "Unknown member";
}

export interface OrgMemberSelectProps {
  /** The selected member's user id, or null for nobody. */
  value: string | null;
  onChange: (userId: string | null) => void;
  /** Label for the "nobody" choice; leave out to make a member required. */
  noneLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}

export function OrgMemberSelect({
  value,
  onChange,
  noneLabel,
  placeholder = "Choose a member",
  disabled,
  className,
  id,
  "aria-label": ariaLabel,
}: OrgMemberSelectProps) {
  const api = useDolfinClient();
  const [members, setMembers] = useState<OrganisationUserResponse[]>([]);

  useEffect(() => {
    let cancelled = false;
    api
      .GET("/v1/users", { params: { query: { Limit: USER_FETCH_LIMIT } } })
      .then(({ data }) => {
        if (!cancelled) setMembers((data?.data ?? []).filter((m) => m.id));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <Select
      value={value ?? (noneLabel ? NONE : undefined)}
      disabled={disabled}
      onValueChange={(v) => onChange(v === NONE ? null : v)}
    >
      <SelectTrigger id={id} aria-label={ariaLabel} className={cn("w-full", className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {noneLabel ? (
          <SelectItem value={NONE} className="text-muted-foreground">
            {noneLabel}
          </SelectItem>
        ) : null}
        {members.map((m) => (
          // Name only: the item text is also what the closed trigger shows.
          <SelectItem key={m.id} value={m.id!}>
            {memberLabel(m)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
